// ABOUTME: TanStack Query hooks for Admin Panel account management (super_admin only).
import { AdminRole } from '../../../types/adminRoles';
import { accountsApi, CreateAccountInput, Account } from '../api/accountsApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const accountKeys = {
  all: ['accounts'] as const,
};

export function useAccounts(enabled = true) {
  return useQuery({
    queryKey: accountKeys.all,
    queryFn: accountsApi.getAll,
    enabled,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAccountInput) => accountsApi.create(data),
    onSuccess: (created) => {
      queryClient.setQueryData<{ accounts: Account[] } | undefined>(
        accountKeys.all,
        (old) => ({ accounts: [...(old?.accounts ?? []), created] }),
      );
    },
  });
}

export function useUpdateAccountRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: AdminRole }) =>
      accountsApi.updateRole(id, role),
    onSuccess: (updated) => {
      queryClient.setQueryData<{ accounts: Account[] } | undefined>(
        accountKeys.all,
        (old) => ({
          accounts: (old?.accounts ?? []).map((account) =>
            account.id === updated.id ? updated : account,
          ),
        }),
      );
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountsApi.remove(id),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<{ accounts: Account[] } | undefined>(
        accountKeys.all,
        (old) => ({
          accounts: (old?.accounts ?? []).filter(
            (account) => account.id !== id,
          ),
        }),
      );
    },
  });
}
